import { useEffect, useRef, useState } from 'react'
import useUploadService from 'hooks/useUploadService'
import { useFileValidation } from 'hooks/useFileValidation'
import { getFileExtension } from 'utils'
import { uploadExecutionFile } from '../api/upload'

export interface IQueuedMedia {
  id: number
  file: File
  preview: string
  state: 'queued' | 'uploading' | 'failed'
}
export const useExecutionUpload = (onUploaded: (url: string) => Promise<void>) => {
  const { getUrlForUploadingFile } = useUploadService()
  const { fileSizeValidation } = useFileValidation()
  const [queue, setQueue] = useState<IQueuedMedia[]>([])
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const controller = useRef<AbortController | null>(null)
  const previews = useRef(new Set<string>())
  const mounted = useRef(true)
  const nextId = useRef(0)

  const remove = (id: number) =>
    setQueue((items) =>
      items.filter((item) => {
        if (item.id !== id) return true
        URL.revokeObjectURL(item.preview)
        previews.current.delete(item.preview)
        return false
      })
    )

  const run = async (items: IQueuedMedia[]) => {
    if (controller.current || !items.length) return
    const abort = new AbortController()
    controller.current = abort
    setBusy(true)
    setProgress({ completed: 0, total: items.length })
    let completed = 0
    for (const item of items) {
      if (abort.signal.aborted) break
      setQueue((q) => q.map((i) => (i.id === item.id ? { ...i, state: 'uploading' } : i)))
      try {
        if (!fileSizeValidation(item.file)) throw new Error('Invalid file size')
        const extension = getFileExtension(item.file.name).toLowerCase()
        const signed = await getUrlForUploadingFile(item.file.name.replace(/\.[^.]+$/, ''), extension)
        if (abort.signal.aborted) break
        await uploadExecutionFile(item.file, signed, abort.signal)
        if (abort.signal.aborted || !mounted.current) break
        await onUploaded(signed.fileLink)
        if (!mounted.current) break
        remove(item.id)
      } catch (error) {
        if (!mounted.current || abort.signal.aborted) break
        setQueue((q) => q.map((i) => (i.id === item.id ? { ...i, state: 'failed' } : i)))
      }
      completed++
      if (mounted.current) setProgress({ completed, total: items.length })
    }
    if (mounted.current) {
      setQueue((q) => q.map((i) => (i.state === 'uploading' ? { ...i, state: 'queued' } : i)))
      setBusy(false)
    }
    controller.current = null
  }

  const add = async (files: File[]) => {
    if (controller.current) return
    const items = files.map((file): IQueuedMedia => {
      const preview = URL.createObjectURL(file)
      previews.current.add(preview)
      return { id: ++nextId.current, file, preview, state: 'queued' }
    })
    setQueue((q) => [...q, ...items])
    await run(items)
  }

  useEffect(
    () => () => {
      mounted.current = false
      controller.current?.abort()
      previews.current.forEach((url) => URL.revokeObjectURL(url))
    },
    []
  )

  return { queue, busy, progress, add, retry: () => run(queue), remove, cancel: () => controller.current?.abort() }
}
